import React, { useState, useRef, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import CircularProgressWithLabel from "../progress/CircularProgressWithLabel";
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
//import quizData from "../../itemtypes.json";
import ApiMap from "../../utils/ApiMap";
import axios from "axios";
import AuthService from "../../utils/AuthService";
import { useMediaQuery } from "@mui/material";
import { parseMfbParts } from "../../utils/mfbUtils";
import GameServer from "../services/GameServer";
import ActivityRenderer from "../items/ActivityRenderer";

const getSentenceTokens = (sentence) => {
  if (typeof sentence !== "string") {
    return [];
  }

  return sentence
    .trim()
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);
};

const normalizeSentenceValue = (sentence) => {
  if (!sentence) return "";
  const tokens = getSentenceTokens(sentence)
    .map((t) => t.trim())
    .filter(Boolean);
  const isArabic = /[\u0600-\u06FF]/.test(sentence);
  // Reverse tokens if it's Arabic and visual order mismatch likely
  const normalized = isArabic ? tokens.reverse().join(" ") : tokens.join(" ");
  return normalized.normalize("NFC").trim();
};

const QuizPage = () => {
  console.log("Quizpage");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [MCMAAnswers, setMCMAAnswers] = useState({});
  const [markupAnswers, setMarkupAnswers] = useState({});
  const [matchAnswers, setMatchAnswers] = useState({});
  const [shuffledWords, setShuffledWords] = useState([]);
  const [rearrangeSelections, setRearrangeSelections] = useState({});
  const [submitted, setSubmitted] = useState({}); // Track submitted questions
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [progressValue, setProgressValue] = useState(0);
  const rationaleRef = useRef(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [matchSelections, setMatchSelections] = useState({});
  const [shuffledNames, setShuffledNames] = useState([]);
  const [shuffledValues, setShuffledValues] = useState([]);
  const [rankingSelections, setRankingSelections] = useState([]);
  const [rankingAnswers, setRankingAnswers] = useState({});
  const [mfbSelections, setMfbSelections] = useState({});
  const [shuffledMfbOptions, setShuffledMfbOptions] = useState([]);
  const [selectedIraabWord, setSelectedIraabWord] = useState(null);
  const [iraabAnswers, setIraabAnswers] = useState({});
  const [quizData, setQuizData] = useState(null);
  const [error, setError] = useState(null); // Error state
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [hasUserInteracted, setHasUserInteracted] = useState(true);
  const location = useLocation();
  const { chapterName } = location.state || {};
  const { gameCode, courseId, moduleId, chapterId, lessonId } = useParams();
  const [skippedQuestions, setSkippedQuestions] = useState([]);
  const [isQuizStarted, setIsQuizStarted] = useState(true);

  const [reviewedContent, setReviewedContent] = useState({});
  const attemptCode = quizData?.AttemptCode;
  const isMobile = useMediaQuery("(max-width:600px)");
  const contentId = lessonId;

  useEffect(() => {
    return () => {
      // cleanup when leaving this page
      localStorage.removeItem("quizUnsaved");
    };
  }, [location.pathname]);

  useEffect(() => {
    if (isQuizStarted && !isLoading && !quizData?.Submitted) {
      localStorage.setItem("quizUnsaved", "true");
    } else {
      localStorage.removeItem("quizUnsaved");
    }
  }, [isQuizStarted, isLoading, quizData]);

  useEffect(() => {
    if (quizData?.Submitted) {
      console.log("Quiz has already been submitted");
      setIsQuizStarted(false);
    } else {
      setIsQuizStarted(true);
    }
  }, [quizData?.Submitted]);

  useEffect(() => {
    if (gameCode && quizData?.Activities?.length > 0) {
      GameServer.notify({
        action: "msgstudentstartedgame",
        channel: gameCode,
        payload: {
          lessonId: contentId,
          ItemCount: quizData.Activities.length,
        },
      });
    }
  }, [gameCode, quizData]);

  //const gameCode = null;

  const colorMap = [
    "#e74c3c", // deep red
    "#27ae60", // dark green
    "#3498db", // strong blue
    "#7f8c8d", // steel gray
    "#f1c40f", // golden yellow
    "#8e44ad", // royal purple
    "#2980b9", // ocean blue
    "#1abc9c", // teal green
    "#c0392b", // dark coral
    "#9b59b6", // violet purple
  ];

  useEffect(() => {
    setQuizData(null);
    let url = `${ApiMap.ASSESSMENT}/course/${courseId}/module/${moduleId}/chapter/${chapterId}/assessment/${contentId}`;
    if (gameCode) {
      url = `${ApiMap.GAME_ASSESSMENT}/${gameCode}/assessment`; // ✅ multiplayer mode
    }

    const fetchData = async () => {
      try {
        const resp = await axios(url, AuthService.getAuthHeader());
        setQuizData(resp.data);
      } catch (err) {
        setError("Failed to load quiz. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [contentId, gameCode]);

  const reFetchQuiz = async () => {
    await fetchQuiz(true);
  };

  const fetchQuiz = async (retake) => {
    setIsLoading(true);
    var url = `${ApiMap.ASSESSMENT}/course/${courseId}/module/${moduleId}/chapter/${chapterId}/assessment/${contentId}`;
    if (retake) {
      url = `${url}?retake=${retake}`;
    }
    try {
      const resp = await axios(url, AuthService.getAuthHeader());
      setQuizData(resp.data);
    } catch (err) {
      setError("Failed to load lesson quiz. Please try again.");
    } finally {
      setIsLoading(false); // Stop loading after fetch completes
    }
  };

  const renderLoading = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        height: "100vh",
      }}
    >
      <CircularProgress size={24} />
    </Box>
  );

  const renderError = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    </Box>
  );

  const currentActivity = quizData?.Activities[currentQuestionIndex];
  // const isLastQuestion =
  //   currentQuestionIndex === quizData?.Activities?.length - 1;
  const options =
    currentActivity?.Type === "Markup" ? currentActivity?.Options : null;
  const types = options
    ? Object.values(options).flatMap((option) =>
        option.map((item) => item.Type)
      )
    : [];
  const typeCount = types.reduce((acc, type) => {
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  const assignColors = (types) => {
    const typeToColor = {};
    let colorIndex = 0;

    types.forEach((type) => {
      if (!typeToColor[type]) {
        // Assign the next color or loop back to the start
        typeToColor[type] = colorMap[colorIndex % colorMap.length];
        colorIndex++;
      }
    });

    return typeToColor;
  };

  // Get unique types
  const uniqueTypes = Array.from(new Set(types));

  // Assign colors to unique types
  const typeToColorMap = assignColors(uniqueTypes);

  const isButtonActive =
    currentActivity?.Type === "Markup"
      ? Object.keys(markupAnswers).length > 0
      : currentActivity?.Type === "Match"
      ? matchAnswers[currentActivity?.ContentId] &&
        Object.keys(matchAnswers[currentActivity?.ContentId]).length > 0
      : currentActivity?.Type === "ReArrange" ||
        currentActivity?.Type === "Ranking"
      ? hasUserInteracted
      : currentActivity?.Type === "MFB"
      ? mfbSelections[currentActivity?.ContentId] &&
        Object.values(mfbSelections[currentActivity?.ContentId]).every(
          (val) => val !== ""
        )
      : currentActivity?.Type === "Iraab"
      ? Object.keys(iraabAnswers).length > 0
      : currentActivity?.Type === "MCMA"
      ? (() => {
          const answer = MCMAAnswers[currentActivity?.ContentId];
          return Array.isArray(answer)
            ? answer.length > 0 // multiple choice case
            : answer !== undefined; // single choice case
        })()
      : false;

  const sanitizeTitle = (title) => {
    if (!title) return "";

    // Remove `autoplay` from <audio> tags
    return title.replace(
      /<audio([^>]*)autoplay([^>]*)>/g,
      "<audio$1$2>" // Keep the audio tag but without autoplay
    );
  };

  useEffect(() => {
    if (submitted[currentActivity?.ContentId] && rationaleRef.current) {
      setTimeout(() => {
        if (rationaleRef.current) {
          // Double check before scrolling
          rationaleRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    }
  }, [submitted, currentActivity?.ContentId]);

  useEffect(() => {
    const totalQuestions = quizData?.Activities?.length || 0;
    const corrects = Object.values(submitted).filter((s) => s.isCorrect).length;

    const progress = totalQuestions > 0 ? (corrects / totalQuestions) * 100 : 0;
    setProgressValue(Math.min(progress, 100));
  }, [submitted]);

  useEffect(() => {
    if (currentActivity?.Options && currentActivity?.Type === "Match") {
      // Shuffle names and values when a new activity is loaded
      const shuffledNames = shuffleArray(
        currentActivity.Options.map((option) => option.Name)
      );
      const shuffledValues = shuffleArray(
        currentActivity.Options.map((option) => option.Value)
      );

      setShuffledNames(shuffledNames);
      setShuffledValues(shuffledValues);
    }
  }, [currentActivity?.Options]); // Dependency ensures it runs only when Options change

  useEffect(() => {
    if (currentActivity?.Options) {
      if (currentActivity.Type === "Ranking") {
        const shuffledRanking = shuffleArray(
          currentActivity.Options.map((option, index) => ({
            ...option,
            Id: option.Id ?? `temp-${index}`, // Ensure Id exists
          }))
        );
        setRankingSelections(shuffledRanking);
      } else if (currentActivity.Type === "ReArrange") {
        const optionsList = Array.isArray(currentActivity.Options)
          ? currentActivity.Options
          : [];
        const baseOption = optionsList.find(
          (option) => typeof option?.Value === "string" && option.Value.trim()
        );
        const baseTokens = getSentenceTokens(baseOption?.Value);
        const shuffledRearrangeWords = shuffleArray([...baseTokens]);
        setShuffledWords(shuffledRearrangeWords);
      } else if (currentActivity.Type === "MFB") {
        const value = currentActivity.Options?.[0]?.Value || "";
        const extractedOptions = parseMfbParts(value).map((part) =>
          Array.isArray(part) ? shuffleArray([...part]) : part
        );

        setShuffledMfbOptions(extractedOptions);
      }
    }
  }, [currentActivity]);

  const handleSkip = (contentId) => {
    setSkippedQuestions((prev) => {
      // add to skipped if not already there
      const updatedSkipped = prev.includes(contentId)
        ? prev
        : [...prev, contentId];

      let nextIndex = currentQuestionIndex + 1;
      const totalQuestions = quizData?.Activities?.length;

      // Find the next unanswered question (ignore submitted only, allow already skipped)
      while (
        nextIndex < totalQuestions &&
        submitted[quizData.Activities[nextIndex].ContentId]
      ) {
        nextIndex++;
      }

      if (nextIndex < totalQuestions) {
        setCurrentQuestionIndex(nextIndex);
      } else {
        // If no unanswered left, go back to first skipped but unsubmitted
        const nextSkippedIndex = quizData?.Activities?.findIndex(
          (q) => updatedSkipped.includes(q.ContentId) && !submitted[q.ContentId]
        );

        if (nextSkippedIndex !== -1) {
          setCurrentQuestionIndex(nextSkippedIndex);
        }
      }

      return updatedSkipped;
    });
  };

  const hasSkippedOrUnsubmitted = quizData?.Activities?.some(
    (act) =>
      !submitted[act.ContentId] && act.ContentId !== currentActivity?.ContentId
  );

  const handleNext = () => {
    const totalQuestions = quizData?.Activities?.length;

    let nextIndex = currentQuestionIndex + 1;

    // First, check if there are any skipped questions that should be answered next
    const skippedIndex = quizData?.Activities?.findIndex(
      (activity) =>
        skippedQuestions.includes(activity.ContentId) &&
        !submitted[activity.ContentId]
    );

    if (skippedIndex !== -1) {
      // If there are skipped questions, move to the first skipped one
      setCurrentQuestionIndex(skippedIndex);
    } else {
      // If no skipped question left, move to the next unanswered one
      while (
        nextIndex < totalQuestions &&
        (submitted[quizData.Activities[nextIndex].ContentId] ||
          skippedQuestions.includes(quizData.Activities[nextIndex].ContentId))
      ) {
        nextIndex++; // Skip over already answered or skipped questions
      }

      if (nextIndex < totalQuestions) {
        // Move to the next unanswered question
        setCurrentQuestionIndex(nextIndex);
      } else {
        // If there are no more unanswered questions, show results
        setShowResults(true);
      }
    }
  };

  const sendUserResp = async (aid, attemptCode, correctCount, startTime) => {
    const userAnswer = {
      crsId: courseId,
      modId: moduleId,
      chpId: chapterId,
      lsnId: contentId,
      attemptCode: attemptCode,
      responseTimeInMillsec: Date.now() - startTime,
      answeredCorrect: correctCount === 1,
      percentCompleted: progressValue,
      gameCode: gameCode || null, // ✅ multiplayer mode
    };

    await axios.post(
      `${ApiMap.ASSESSMENT}/item/${aid}/attempt`,
      userAnswer,
      AuthService.getAuthHeader()
    );

    if (gameCode) {
      console.log("🎯 Sending msganswersubmitted via GameServer");
      GameServer.notify(
        gameCode, // ✅ channel
        "msganswersubmitted", // ✅ event
        { ...userAnswer, aid }, // ✅ payload
        {} // ✅ handlers (optional)
      );
    }
  };

  const completeQuiz = async () => {
    try {
      let assessment = {
        crsId: String(courseId),
        modId: String(moduleId),
        chpId: String(chapterId),
        lsnId: String(contentId),
        attemptCode: quizData?.AttemptCode,
        gameCode: gameCode || null,
      };

      await axios.post(
        `${ApiMap.ASSESSMENT}/submitassessment`,
        assessment,
        AuthService.getAuthHeader()
      );

      if (gameCode) {
        GameServer.notify({
          action: "studentcompleted",
          channel: gameCode,
          payload: assessment,
        });
      }

      console.log("Assessment submitted ✅");
    } catch (err) {
      console.error("Error submitting assessment", err);
    }
  };

  const handleOptionChange = (activityId, optionId, selectedValue) => {
    const activity = quizData?.Activities.find(
      (q) => q.ContentId === activityId
    );
    if (!activity) return;

    if (activity.Type === "MCMA") {
      const correctAnswersCount = (activity.Options || []).filter(
        (option) => option.IsCorrect
      ).length;

      setMCMAAnswers((prev) => {
        if (correctAnswersCount === 1) {
          // Radio - single choice
          return {
            ...prev,
            [activityId]: optionId, // store a single value
          };
        } else {
          // Checkbox - multiple choice
          const existing = prev[activityId] || [];
          if (existing.includes(optionId)) {
            return {
              ...prev,
              [activityId]: existing.filter((id) => id !== optionId),
            };
          } else {
            return {
              ...prev,
              [activityId]: [...existing, optionId],
            };
          }
        }
      });
    } else if (activity.Type === "Match") {
      setMatchAnswers((prev) => ({
        ...prev,
        [activityId]: {
          ...prev[activityId],
          [optionId]: selectedValue,
        },
      }));
    } else if (activity.Type === "ReArrange") {
      const normalizedSelection = normalizeSentenceValue(selectedValue);
      setRearrangeSelections((prev) => ({
        ...prev,
        [activityId]: normalizedSelection,
      }));
    } else if (activity.Type === "Ranking") {
      // Save the answer ONLY, not the drag UI state
      setRankingAnswers((prev) => ({
        ...prev,
        [activityId]: selectedValue, // selectedValue = ["A","B","C"]
      }));
    } else if (activity.Type === "MFB") {
      setMfbSelections((prev) => ({
        ...prev,
        [activityId]: {
          ...prev[activityId],
          [optionId]: selectedValue, // Stores selected value for each dropdown
        },
      }));
    } else if (activity.Type === "Iraab") {
      setIraabAnswers((prev) => ({
        ...prev,
        [activityId]: {
          ...prev[activityId],
          [optionId]: selectedValue, // Stores selected haraka for each word
        },
      }));
    }
  };

  const handleSelectChange = (optionId, selectedValue) => {
    const updatedSelections = {
      ...matchSelections,
      [optionId]: selectedValue,
    };
    setMatchSelections(updatedSelections);

    // Notify parent of the change
    handleOptionChange(currentActivity?.ContentId, optionId, selectedValue);
  };

  const handleSubmit = (activityId) => {
    const startTime = Date.now();
    const gameCode = "";
    const activity = quizData.Activities.find(
      (act) => act.ContentId === activityId
    );
    if (!activity) return;

    let correctCount = 0;

    // ===== Evaluate correctness by type =====
    if (activity.Type === "MCMA") {
      const selected = MCMAAnswers[activityId];
      const selectedAnswers = Array.isArray(selected)
        ? selected
        : selected !== undefined
        ? [selected]
        : [];

      const correctAnswers = activity.Options.filter(
        (opt) => opt.IsCorrect
      ).map((opt) => opt.id);
      const correctSelections = selectedAnswers.filter((selected) =>
        correctAnswers.includes(selected)
      ).length;
      const wrongSelections = selectedAnswers.filter(
        (selected) => !correctAnswers.includes(selected)
      ).length;

      if (wrongSelections > 0) {
        correctCount = 0;
      } else if (correctSelections === correctAnswers.length) {
        correctCount = 1;
      } else {
        correctCount = correctSelections / correctAnswers.length;
      }
    }

    if (activity.Type === "Markup") {
      let correctSelections = 0;
      Object.keys(activity.Options).forEach((optionKey) => {
        const index = Number(optionKey) - 1;
        const correctTypes = activity.Options[optionKey].map(
          (item) => item.Type
        );
        const userSelections = markupAnswers[index] || [];
        const userSelectedTypes = userSelections.map((entry) => entry.type);

        if (
          userSelectedTypes.length === correctTypes.length &&
          JSON.stringify(userSelectedTypes.slice().sort()) ===
            JSON.stringify(correctTypes.slice().sort())
        ) {
          correctSelections += 1;
        }
      });
      if (correctSelections === Object.keys(activity.Options).length) {
        correctCount = 1;
      }
    }

    if (activity.Type === "Match") {
      const matchSelections = matchAnswers[activityId];
      const isCorrect = activity.Options.every((option) =>
        Object.values(matchSelections).includes(option.Value)
      );
      if (isCorrect) correctCount = 1;
    }

    if (activity.Type === "ReArrange") {
      const userSentence = normalizeSentenceValue(
        rearrangeSelections[activity.ContentId]
      );

      const correctSentences = Array.isArray(activity.Options)
        ? activity.Options.map((option) =>
            normalizeSentenceValue(option?.Value)
          )
        : [];

      console.log("User:", userSentence);
      console.log("Correct:", correctSentences);

      if (
        userSentence &&
        correctSentences.some(
          (sentence) => sentence && sentence === userSentence
        )
      ) {
        correctCount = 1;
      }
    }

    if (activity.Type === "Ranking") {
      const userOrder = rankingAnswers[activityId] || [];
      const correctOrder = activity.Options.map((opt) => opt.Value);
      const isCorrect =
        JSON.stringify(userOrder) === JSON.stringify(correctOrder);
      if (isCorrect) correctCount = 1;
    }

    if (activity.Type === "MFB") {
      const userSelectionsObj = mfbSelections[activityId] || {};
      const userSelections = Object.values(userSelectionsObj).map((word) =>
        word.trim()
      );
      const correctAnswers = activity.Options[0].Value.split(" ")
        .map((chunk) => (chunk.includes("/") ? chunk.split("/")[0] : null))
        .filter(Boolean)
        .map((word) => word.trim());

      const isCorrect =
        userSelections.length === correctAnswers.length &&
        userSelections.every((word, i) => word === correctAnswers[i]);
      if (isCorrect) correctCount = 1;
    }

    if (activity.Type === "Iraab") {
      const correctWords = activity.Iraab.Value.split(" ");
      let allCorrect = true;
      correctWords.forEach((correctWord, index) => {
        const harakaatPattern = /[ًٌٍَُِْ]$/;
        const userWord = iraabAnswers[index] || "";
        if (!harakaatPattern.test(correctWord)) return;
        if (userWord.trim() !== correctWord.trim()) {
          allCorrect = false;
        }
      });

      if (allCorrect) correctCount = 1;
    }

    // ===== Update submission state =====
    const newSubmitted = {
      ...submitted,
      [activityId]: {
        isSubmitted: true,
        isCorrect: correctCount >= 1,
      },
    };

    setSubmitted(newSubmitted);

    console.log(correctCount);

    if (correctCount > 0) {
      setCorrectAnswers((prev) => prev + correctCount);
    }

    sendUserResp(
      activity.ContentId,
      quizData.AttemptCode,
      correctCount,
      startTime,
      gameCode
    );

    const allActivities = quizData?.Activities || [];

    // find if there are any unanswered (not skipped, not submitted yet)
    const unanswered = allActivities.filter(
      (act) =>
        !newSubmitted[act.ContentId]?.isSubmitted &&
        !skippedQuestions.includes(act.ContentId)
    );

    // find skipped but unsubmitted
    const unsubmittedSkipped = skippedQuestions.filter(
      (id) => !newSubmitted[id]?.isSubmitted
    );

    const allSubmitted = allActivities.every(
      (activity) => newSubmitted[activity.ContentId]?.isSubmitted
    );

    if (allSubmitted) {
      setIsQuizStarted(false);

      if (unanswered.length > 0) {
        // 👉 still unanswered left (non-skipped), go to the first one
        const firstUnansweredIndex = allActivities.findIndex((q) =>
          unanswered.some((u) => u.ContentId === q.ContentId)
        );
        if (firstUnansweredIndex !== -1) {
          setCurrentQuestionIndex(firstUnansweredIndex);
          return;
        }
      } else if (unsubmittedSkipped.length > 0) {
        // 👉 if all normal answered, now revisit skipped
        const firstSkippedIndex = allActivities.findIndex((q) =>
          unsubmittedSkipped.includes(q.ContentId)
        );
        if (firstSkippedIndex !== -1) {
          setCurrentQuestionIndex(firstSkippedIndex);
          return;
        }
      }

      // after skipped are also done → correctness flow
      const incorrectActivities = allActivities.filter(
        (activity) => !newSubmitted[activity.ContentId]?.isCorrect
      );

      if (incorrectActivities.length === 0) {
        setShowResults(true);
        completeQuiz();
      } else {
        const updatedSubmitted = { ...newSubmitted };
        incorrectActivities.forEach((act) => {
          if (act.ContentId !== activityId) {
            delete updatedSubmitted[act.ContentId];
          }
        });
        setSubmitted(updatedSubmitted);

        const firstIncorrectIndex = allActivities.findIndex((a) =>
          incorrectActivities.some((inc) => inc.ContentId === a.ContentId)
        );
        if (firstIncorrectIndex !== -1) {
          setCurrentQuestionIndex(firstIncorrectIndex);
        }
      }
    }
  };

  const handleRetake = (activityId) => {
    // Remove the submission status
    setSubmitted((prev) => {
      const updated = { ...prev };
      delete updated[activityId];
      return updated;
    });

    // Reset MCMA answers if applicable
    setMCMAAnswers((prev) => {
      const updated = { ...prev };
      delete updated[activityId];
      return updated;
    });

    setReviewedContent((prev) => {
      const updated = { ...prev };
      delete updated[contentId];
      return updated;
    });
  };

  const handleRetry = (activityId) => {
    // Remove the submission status
    setSubmitted((prev) => {
      const updated = { ...prev };
      delete updated[activityId];
      return updated;
    });
  };

  const handleTryAgain = () => {
    setMCMAAnswers({});
    setSubmitted({});
    setCorrectAnswers(0);
    setProgressValue(0);
    setCurrentQuestionIndex(0);
    setShowResults(false);
    setMarkupAnswers({});
    setMatchSelections({});
    setHasUserInteracted(false);
    setMatchAnswers({});
  };

  const shuffleArray = (array) => {
    return array.sort(() => Math.random() - 0.5); // Simple shuffle function
  };

  if (isLoading) return renderLoading(); // Show loading spinner
  if (error) return renderError(); // Show error message

  return (
    <Box sx={{ maxWidth: 950, margin: "0 auto" }}>
      <Card
        sx={{
          padding: 2,
          maxWidth: 850,
          margin: "0 auto",
        }}
      >
        <Box>
          {currentActivity?.ContentId && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.75rem",
                fontStyle: "italic",
                color: "text.secondary",
              }}
            >
              ({currentActivity?.ContentId})
            </Typography>
          )}
        </Box>
        {quizData?.Activities?.length !== 0 ? (
          !showResults ? (
            <>
              <CardContent sx={{ padding: 3 }}>
                {currentActivity?.Type === "Markup" && (
                  <Box
                    sx={{
                      marginBottom: "20px",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 2,
                      justifyContent: "center",
                    }}
                  >
                    {Object.entries(typeCount).map(([type, count]) => {
                      const isSelected = selectedColor === typeToColorMap[type];

                      return (
                        <Button
                          key={type}
                          sx={{
                            backgroundColor: typeToColorMap[type] || "default",
                            color: "#000",
                            border: isSelected ? "2px solid #000" : "none",
                            "&:hover": {
                              backgroundColor:
                                typeToColorMap[type] || "default",
                            },
                            minWidth: "100px",
                            flex: "1 1 auto",
                          }}
                          onClick={() =>
                            !submitted[currentActivity?.ContentId] &&
                            setSelectedColor(typeToColorMap[type])
                          }
                          disabled={submitted[currentActivity?.ContentId]}
                        >
                          {`${count} ${type}`}
                        </Button>
                      );
                    })}
                  </Box>
                )}

                {/* ====== UNIVERSAL QUESTION RENDERER ====== */}
                <ActivityRenderer
                  activity={currentActivity}
                  index={currentQuestionIndex}
                  total={quizData?.Activities?.length}
                  submitted={submitted[currentActivity?.ContentId]}
                  answers={MCMAAnswers}
                  preview={false}
                  // Pass required handlers:
                  handleOptionChange={handleOptionChange}
                  shuffledNames={shuffledNames}
                  shuffledValues={shuffledValues}
                  shuffledWords={shuffledWords}
                  setShuffledWords={setShuffledWords}
                  matchSelections={matchSelections}
                  rearrangeSelections={rearrangeSelections}
                  rankingSelections={rankingSelections}
                  rankingAnswers={rankingAnswers}
                  setRankingSelections={setRankingSelections}
                  mfbSelections={mfbSelections}
                  setMfbSelections={setMfbSelections}
                  shuffledMfbOptions={shuffledMfbOptions}
                  markupAnswers={markupAnswers}
                  setMarkupAnswers={setMarkupAnswers}
                  selectedColor={selectedColor}
                  typeToColorMap={typeToColorMap}
                  rationaleRef={rationaleRef}
                  reviewedContent={reviewedContent}
                  setReviewedContent={setReviewedContent}
                  iraabAnswers={iraabAnswers}
                  selectedIraabWord={selectedIraabWord}
                  setSelectedIraabWord={setSelectedIraabWord}
                  setIraabAnswers={setIraabAnswers}
                  handleSelectChange={handleSelectChange}
                />

                {/* ====== Submission Buttons ====== */}
                {submitted[currentActivity?.ContentId] && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    {quizData?.Activities.every(
                      (activity) => submitted[activity.ContentId]?.isSubmitted
                    ) ? (
                      Object.values(submitted).every((s) => s.isCorrect) ? (
                        <Button
                          onClick={handleNext}
                          variant="contained"
                          color="primary"
                          sx={{
                            height: 30,
                            padding: "16px 42px",
                            fontSize: "0.75rem",
                            borderRadius: "15px",
                          }}
                        >
                          Finish
                        </Button>
                      ) : (
                        <Button
                          onClick={() =>
                            handleRetake(currentActivity?.ContentId)
                          }
                          variant="contained"
                          color="secondary"
                          sx={{
                            height: 30,
                            padding: "16px 24px",
                            fontSize: "0.75rem",
                            borderRadius: "15px",
                          }}
                        >
                          Try Again
                        </Button>
                      )
                    ) : (
                      <Button
                        onClick={handleNext}
                        variant="contained"
                        color="primary"
                        sx={{
                          height: 30,
                          padding: "16px 42px",
                          fontSize: "0.75rem",
                          borderRadius: "15px",
                        }}
                      >
                        Next
                      </Button>
                    )}

                    {submitted[currentActivity?.ContentId]?.isSubmitted &&
                      !submitted[currentActivity?.ContentId]?.isCorrect &&
                      reviewedContent?.[currentActivity?.ContentId] && (
                        <Button
                          onClick={() =>
                            handleRetake(currentActivity?.ContentId)
                          }
                          variant="contained"
                          color="secondary"
                          sx={{
                            height: 30,
                            padding: "16px 24px",
                            fontSize: "0.75rem",
                            borderRadius: "15px",
                          }}
                        >
                          Retake
                        </Button>
                      )}
                  </Box>
                )}
              </CardContent>

              <CardActions sx={{ justifyContent: "center" }}>
                {!submitted[currentActivity?.ContentId] &&
                  quizData?.Activities?.length > 1 &&
                  hasSkippedOrUnsubmitted && (
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{
                        height: 30,
                        fontSize: "0.75rem",
                        padding: "16px 42px",
                        borderRadius: "15px",
                      }}
                      onClick={() => handleSkip(currentActivity?.ContentId)}
                    >
                      Skip
                    </Button>
                  )}

                {!submitted[currentActivity?.ContentId] && (
                  <Button
                    onClick={() => handleSubmit(currentActivity?.ContentId)}
                    variant="contained"
                    color="primary"
                    disabled={!isButtonActive}
                    sx={{
                      height: 30,
                      fontSize: "0.75rem",
                      padding: "16px 42px",
                      borderRadius: "15px",
                    }}
                  >
                    Submit
                  </Button>
                )}
              </CardActions>
            </>
          ) : (
            /* Results Page (unchanged) */
            quizData?.Activities?.every(
              (activity) =>
                submitted[activity.ContentId] ||
                skippedQuestions.includes(activity.ContentId)
            ) && (
              <Box sx={{ textAlign: "center", mt: 3 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  sx={{ marginBottom: 2 }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      textAlign: "left",
                      fontWeight: 600,
                      color: "secondary.main",
                    }}
                  >
                    Chapter : {chapterName}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    Quiz Results
                  </Typography>
                </Box>

                <CircularProgressWithLabel
                  progressStep={10}
                  maxValue={100}
                  value={progressValue}
                  handleTryAgain={handleTryAgain}
                />
              </Box>
            )
          )
        ) : (
          <Box style={{ margin: "75px 25px 25px 25px" }}>
            <Typography sx={{ mb: "10px" }}>
              You have completed this assessment before.
            </Typography>
            <Button
              sx={{ background: "primary.main" }}
              variant="contained"
              color="primary"
              onClick={reFetchQuiz}
            >
              RETAKE TEST
            </Button>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default QuizPage;
